import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField, Card, Grid, Divider, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Button, Alert, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import { useUser } from '../../context/userContext';
import { getAgentDetailsByAgentId } from '../../api/userApi';
import useCscProposalMath, {
    CSC_PROPOSAL_TARIFFS,
    DEFAULT_PRICE_PER_KWP,
    DEFAULT_OM_PER_KWP_PER_YEAR,
    getMaintenanceForKwp,
    defaultSareTariffByKwp,
} from '../../hooks/useCscProposalMath';
import { commercialPricePerKwp, defaultBrandForKwp, availableBrandsForKwp } from '../../hooks/useRscPricing';
import { buildCscVarMap, generateCscProposalPdf, cscSubstitute, postProcessCscProposal, autoInverterKw, todayDMY, plus30DMY, MY_STATE_GHI } from './cscProposalEngine';
import { sstOn } from '../../utils/sst';
import { batteryCostFor } from '../../utils/cscSizing';

const PP = "'Poppins', sans-serif";
const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
    purple: '#7C3AED',
};

const Label = ({ children, required }) => (
    <Typography sx={{ fontSize: '0.74rem', fontWeight: 500, color: '#374151', mb: 0.4, fontFamily: PP }}>
        {children}{required && <span style={{ color: '#EF4444' }}> *</span>}
    </Typography>
);

const SectionTitle = ({ children }) => (
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1.2, mt: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {children}
    </Typography>
);

const fmt = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt2 = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtPct = (n) => Number(n || 0).toLocaleString('en-MY', { style: 'percent', maximumFractionDigits: 1 });

// Generate an inline SVG avatar with the user's initials, returned as
// a data URL safe to drop into <img src="">. Used as the prepared-by
// photo fallback when the user hasn't uploaded a profile photo — so
// non-photo users don't end up displaying jin-sheng.jpg by accident.
function initialsAvatarDataUrl(name) {
    const initials = (name || '?').trim().split(/\s+/)
        .filter(Boolean).slice(0, 2)
        .map(w => w.charAt(0).toUpperCase()).join('') || '?';
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>` +
        `<rect width='100' height='100' fill='%231A8CFF'/>` +
        `<text x='50' y='66' font-size='44' font-family='Poppins,sans-serif' font-weight='700' text-anchor='middle' fill='white'>${initials}</text>` +
        `</svg>`;
    return `data:image/svg+xml;utf8,${svg}`;
}

// Map the current user's roles to a customer-facing title for the
// proposal's "Prepared By" chip. Sales-facing roles take priority over
// admin labels because a Super Admin + Leader (e.g. the project owner)
// should appear to the customer as a Sales Leader, not an Admin.
function deriveSalesTitle(roles = []) {
    const r = Array.isArray(roles) ? roles : [];
    if (r.includes('Leader')) return 'Sales Leader';
    if (r.includes('Sales Agent')) return 'Sales Consultant';
    if (r.includes('In-house Agent')) return 'Solar Consultant';
    if (r.includes('Referral')) return 'Referral Partner';
    if (r.includes('Super Admin')) return 'Super Admin';
    if (r.includes('Admin') || r.includes('Astern Admin') || r.includes('Group Admin')) return 'Admin';
    if (r.includes('Accountant')) return 'Accountant';
    return 'Solar Consultant';
}

// ─────────────────────────────────────────────────────────────────────────

export default function CscProposalTab({ sharedForm = {}, atapPrices = [], sharedPhaseMode = 'single' } = {}) {
    const { user } = useUser();

    // ── Customer / Site ──
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');

    // ── System ──
    const [kwp, setKwp] = useState('');
    const [panels, setPanels] = useState('');
    const [panelWatt, setPanelWatt] = useState('650');
    const [inverterBrand, setInverterBrand] = useState('goodwe');         // single — drives commercial pricing
    const [inverterBrands, setInverterBrands] = useState(['goodwe']);     // array — drives proposal display label (multi-select)
    const [brandTouched, setBrandTouched] = useState(false);
    const [inverterKw, setInverterKw] = useState('');
    const [inverterKwTouched, setInverterKwTouched] = useState(false);
    const [batteryKwh, setBatteryKwh] = useState('');   // Optional battery storage capacity (kWh)
    const [priceMode, setPriceMode] = useState('kwp');     // 'kwp' or 'total'
    const [pricePerKwp, setPricePerKwp] = useState('');
    const [systemCost, setSystemCost] = useState('');
    const [tariffRate, setTariffRate] = useState(0.515);
    const [omAnnual, setOmAnnual] = useState('');
    const [addOnPrice, setAddOnPrice] = useState('');         // Optional add-on fee added on top of system price (RM)
    const [siteState, setSiteState] = useState('');           // Malaysia state code; drives irradiance page GHI lookup
    const [sitePhotos, setSitePhotos] = useState([]);         // Up to 4 photo data URLs for the Site Design page
    const [maxDemandKw, setMaxDemandKw] = useState('');       // Site Maximum Demand (kW) — drives SELCO vs ATAP recommendation

    // ── Financing toggles ──
    const [includeOutright, setIncludeOutright] = useState(true);
    const [includeSARE, setIncludeSARE] = useState(true);
    // 10-year SARE term — independent toggle (user 2026-08-23).
    const [includeSARE10, setIncludeSARE10] = useState(true);
    const [sareIncludeTouched, setSareIncludeTouched] = useState(false);
    const [includeLoan, setIncludeLoan] = useState(true);
    const [includeCA, setIncludeCA] = useState(true);
    const [includeITA, setIncludeITA] = useState(false);
    const [itaTouched, setItaTouched] = useState(false);
    const [sareTariffOverride, setSareTariffOverride] = useState('');
    const [loanPrincipal, setLoanPrincipal] = useState('');
    const [loanRate, setLoanRate] = useState('5.0');     // p.a. (reducing balance PMT)
    const [loanTermMonths, setLoanTermMonths] = useState('84');  // 7 years

    // ── Sales person prefill ──
    const [sales, setSales] = useState({ name: '', title: 'Solar Consultant', email: '', whatsapp: '', photoUrl: '' });

    // ── Output state ──
    const [lang, setLang] = useState('en');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    // Auto-default system price when kWp / brand / panels change (only if user
    // hasn't manually edited the price). Uses the commercial tier:
    //   <26 kWp  → resi per-kWp × kWp (Solis subtracts RM 50/kWp)
    //   ≥26 kWp  → brand-tier per-kWp
    // Keeps pricePerKwp and systemCost in sync.
    const [systemCostTouched, setSystemCostTouched] = useState(false);
    useEffect(() => {
        const k = Number(kwp) || 0;
        if (!k) return;

        if (!systemCostTouched) {
            let perKwp = 0;
            // Battery requires a Hybrid inverter (GoodWe only); otherwise String.
            const invType = (parseFloat(batteryKwh) > 0) ? 'hybrid' : 'string';
            if (k <= 30) {
                const p = parseInt(panels, 10);
                const match = (atapPrices || []).find(r => r.panels === p);
                if (match) {
                    const primary = sharedPhaseMode === '3phase' ? match.cash3p : match.cash;
                    const secondary = sharedPhaseMode === '3phase' ? match.cash : match.cash3p;
                    const totalResi = primary > 0 ? primary : secondary;
                    if (totalResi > 0) {
                        perKwp = commercialPricePerKwp(k, inverterBrand, invType, totalResi / match.kwp);
                    }
                }
                if (!perKwp) perKwp = DEFAULT_PRICE_PER_KWP;  // fallback if no panel match yet
            } else {
                perKwp = commercialPricePerKwp(k, inverterBrand, invType, 0) || DEFAULT_PRICE_PER_KWP;
            }
            setPricePerKwp(String(Math.round(perKwp)));
            setSystemCost(String(Math.round(k * perKwp)));
            return;
        }

        // After user edits: keep the OTHER field in sync as kWp changes
        if (priceMode === 'kwp' && pricePerKwp) {
            setSystemCost(String(Math.round(k * Number(pricePerKwp))));
        } else if (priceMode === 'total' && systemCost) {
            setPricePerKwp(String(Math.round(Number(systemCost) / k)));
        }
    }, [kwp, panels, inverterBrand, batteryKwh, atapPrices, sharedPhaseMode, systemCostTouched]); // eslint-disable-line react-hooks/exhaustive-deps

    // Suggest the default brand when kWp moves into a new tier — but ONLY
    // if the user hasn't explicitly picked one. Per user (2026-05-21), big
    // systems should still be free to pick GoodWe/Solis in the proposal.
    useEffect(() => {
        const k = Number(kwp) || 0;
        if (k <= 0 || brandTouched) return;
        const def = defaultBrandForKwp(k);
        setInverterBrand(def);
        setInverterBrands([def]);
    }, [kwp, brandTouched]);

    // Cascade panels → kWp (panels × panelWatt / 1000). User can still type
    // kWp directly without it being overridden.
    useEffect(() => {
        const p = parseFloat(panels) || 0;
        const wp = parseFloat(panelWatt) || 0;
        if (p > 0 && wp > 0) {
            const newKwp = +(p * wp / 1000).toFixed(2);
            setKwp(String(newKwp));
        }
    }, [panels, panelWatt]);

    // Auto-fill inverter size from kWp via the tier table (matches sizing
    // tab). User edits set inverterKwTouched, which freezes the value.
    useEffect(() => {
        if (inverterKwTouched) return;
        const auto = autoInverterKw(kwp, atapPrices, sharedPhaseMode);
        if (auto > 0) setInverterKw(String(auto));
    }, [kwp, inverterKwTouched, atapPrices, sharedPhaseMode]);

    // Auto-default O&M cost when kWp changes (only if user hasn't overridden)
    const [omTouched, setOmTouched] = useState(false);
    useEffect(() => {
        if (!omTouched && kwp) {
            setOmAnnual(String(getMaintenanceForKwp(Number(kwp)))); // tier-based, mirrors the CSC calculator
        }
    }, [kwp, omTouched]);

    // GITA available ONLY for systems ≥ 40 kWp — below that it's forced OFF and greyed out.
    // At/above 40 kWp it defaults ON until the user toggles it. (user 2026-07-25)
    useEffect(() => {
        const k = Number(kwp) || 0;
        if (k < 40) { setIncludeITA(false); return; }
        if (itaTouched) return;
        setIncludeITA(true);
    }, [kwp, itaTouched]);

    // SARE on by default; only auto-off once a real system under 40 kWp is known
    // (k=0 = not yet computed → stays on). (user 2026-07-23)
    useEffect(() => {
        if (sareIncludeTouched) return;
        const k = Number(kwp) || 0;
        setIncludeSARE(k === 0 || k >= 42);
    }, [kwp, sareIncludeTouched]);

    // What the SARE rate has to recover on top of the panel system: the battery and the
    // flat add-on price. (user 2026-10-14: "any addon including battery")
    const _sareAddOnCost = batteryCostFor(parseFloat(batteryKwh) || 0) + (parseFloat(addOnPrice) || 0);

    // Auto-default SARE tariff (tier by kWp + the add-on uplift) when user hasn't overridden.
    // String(), not toFixed(2): an uplift of RM0.015 is a real 0.375 and must not round to 0.38.
    const [sareTouched, setSareTouched] = useState(false);
    useEffect(() => {
        if (sareTouched) return;
        const tier = defaultSareTariffByKwp(Number(kwp) || 0, 20, _sareAddOnCost);
        setSareTariffOverride(tier > 0 ? String(tier) : '');
    }, [kwp, _sareAddOnCost, sareTouched]);

    // Inherit from System Size tab (sharedForm) when proposal fields are still blank
    const [kwpTouched, setKwpTouched] = useState(false);
    const [tariffTouched, setTariffTouched] = useState(false);
    const [customerTouched, setCustomerTouched] = useState(false);
    useEffect(() => {
        if (!kwpTouched && sharedForm.kwp && !kwp) {
            setKwp(String(sharedForm.kwp));
        }
        if (!brandTouched && sharedForm.inverterBrand) {
            setInverterBrand(sharedForm.inverterBrand);
        }
        // Sizing tab stores inverter as `kw`. Inherit only if the user
        // hasn't touched the proposal's inverter field yet — once they do,
        // their choice wins over the upstream value.
        if (!inverterKwTouched && sharedForm.kw && !inverterKw) {
            setInverterKw(String(sharedForm.kw));
        }
        if (sharedForm.panels && !panels) {
            setPanels(String(sharedForm.panels));
        }
        if (sharedForm.panelWatt) {
            setPanelWatt(String(sharedForm.panelWatt));
        }
        if (!tariffTouched && sharedForm.tariffType) {
            const t = parseFloat(sharedForm.tariffType);
            if (t > 0) setTariffRate(t);
        }
        if (!customerTouched && sharedForm.companyName && !customerName) {
            setCustomerName(sharedForm.companyName);
        }
        // System cost: pull both per-kWp and total from System Size; default mode
        // mirrors whichever the user picked in that tab.
        if (!systemCostTouched && sharedForm.kwp) {
            const total = parseFloat(sharedForm.totalSolarPrice);
            const perKwp = parseFloat(sharedForm.pricePerKwp);
            const k = parseFloat(sharedForm.kwp);
            if (perKwp > 0) {
                setPricePerKwp(String(perKwp));
                setSystemCost(String(Math.round(perKwp * k)));
                setPriceMode('kwp');
            } else if (total > 0) {
                setSystemCost(String(Math.round(total)));
                setPricePerKwp(String(Math.round(total / k)));
                setPriceMode('total');
            }
        }
    }, [sharedForm.kwp, sharedForm.tariffType, sharedForm.companyName, sharedForm.totalSolarPrice, sharedForm.pricePerKwp, sharedForm.inverterBrand, sharedForm.panels, sharedForm.panelWatt, sharedForm.kw]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sales person prefill (mirrors RSC pattern)
    useEffect(() => {
        if (!user) return;
        // Surname first: "STEVEN LING LEE HOCK" not "LEE HOCK STEVEN LING".
        // Matches the Chinese / Malaysian naming convention used here.
        const fullName = `${user.lastName || ''} ${user.firstName || ''}`.trim() || user.username || '';
        const title = deriveSalesTitle(user.roles);
        const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';
        // Fall back to an initials avatar (not jin-sheng.jpg) so a user
        // without an uploaded profile photo doesn't end up with someone
        // else's face on their proposal.
        const defaultPhoto = initialsAvatarDataUrl(fullName);
        const photoUrl = user.profilePhotoUrl ? `${apiBase}${user.profilePhotoUrl}` : defaultPhoto;
        setSales((p) => ({ ...p, name: fullName, title, photoUrl }));
        if (user.userId) {
            getAgentDetailsByAgentId(user.userId).then((res) => {
                const d = res?.data || {};
                const dPhoto = d.profilePhotoUrl ? `${apiBase}${d.profilePhotoUrl}` : photoUrl;
                setSales((p) => ({
                    ...p,
                    email: d.email || '',
                    whatsapp: d.phoneNumber || d.phone || '',
                    photoUrl: dPhoto,
                }));
            }).catch(() => {});
        }
    }, [user]);

    // ── Math ──
    const math = useCscProposalMath({
        kwp: parseFloat(kwp) || 0,
        systemCost: parseFloat(systemCost) || 0,
        tariffRate,
        omAnnual: parseFloat(omAnnual) || 0,
        includeCA,
        includeITA,
        includeOutright,
        includeSARE,
        includeLoan,
        sareTariffRate: parseFloat(sareTariffOverride) || undefined,
        // Backstop only — the field above already carries the uplift.
        sareAddOnCost: _sareAddOnCost,
        loanPrincipal: parseFloat(loanPrincipal) || undefined,
        loanRate: parseFloat(loanRate) / 100 || 0.07,
        loanTermMonths: parseInt(loanTermMonths, 10) || 120,
    });

    // ── Headline KPIs for live preview ──
    const headlines = useMemo(() => {
        if (math.invalid) return null;
        return {
            yr1Gen: math.yr1Generation,
            yr1Saving: math.yr1AnnualSaving,
            payback: math.outrightBreakeven,
            irr: math.outrightIRR,
            total20yr: math.total20yrOutright,
            avgMonthly: (math.years?.[0]?.grossRevenue || 0) / 12,
            co2: math.co2TonnesPerYear,
            trees: math.treesSavedPerYear,
            lcoe: math.lcoe,
        };
    }, [math]);

    // ── PDF generation ──
    const generatePdf = async () => {
        if (!customerName.trim()) { setError('Customer name is required.'); return; }
        if (!kwp || !systemCost) { setError('System kWp and price are required.'); return; }
        setGenerating(true); setError('');
        const map = buildCscVarMap({
            customer: { name: customerName, phone: customerPhone, email: customerEmail, address: customerAddress },
            system: { kwp, systemCost: Number(systemCost), tariffRate, omAnnual: Number(omAnnual), inverterKw: Number(inverterKw), inverterBrand, inverterBrands, panelWatt: Number(panelWatt) || 650, atapPrices, phaseMode: sharedPhaseMode },
            math,
            sales,
            dates: { issued: todayDMY(), valid: plus30DMY() },
            financing: { loanPrincipal: parseFloat(loanPrincipal) || undefined, includeSARE, includeSARE10, includeOutright, includeLoan },
            addOn: parseFloat(addOnPrice) || 0,
            site: siteState,
            photos: sitePhotos,
            maxDemand: parseFloat(maxDemandKw) || 0,
            batteryKwh: parseFloat(batteryKwh) || 0,
        });
        const r = await generateCscProposalPdf(map, {
            lang, includeSARE, includeSARE10, includeOutright, includeLoan,
            includePhotos: sitePhotos.length > 0,
            customerName, kwp,
        });
        if (!r.ok) setError(r.error);
        setGenerating(false);
    };

    // ── Print preview (vector-quality PDF via browser Save As PDF) ──
    const printPreview = async () => {
        if (!customerName.trim()) { setError('Customer name is required.'); return; }
        if (!kwp || !systemCost) { setError('System kWp and price are required.'); return; }
        setError('');
        try {
            const cacheBust = `?v=${Date.now()}`;
            const tplUrl = (lang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                          : lang === 'my' ? '/templates/astern-proposal-csc-my.html'
                          : '/templates/astern-proposal-csc.html') + cacheBust;
            let res = await fetch(tplUrl);
            let tpl = res.ok ? await res.text() : '';
            // Same fallback as generatePdf: also fall back when nginx serves
            // the SPA index.html for a missing localized template (HTTP 200 but no .page divs).
            const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
            if (lang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
                res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
                tpl = res.ok ? await res.text() : '';
            }
            if (!res.ok || !looksLikeTemplate(tpl)) {
                throw new Error(`Failed to fetch template (HTTP ${res.status})`);
            }
            const origin = window.location.origin;
            tpl = tpl
                .split("src='./").join(`src='${origin}/templates/`)
                .split('src="./').join(`src="${origin}/templates/`)
                .split("url('./").join(`url('${origin}/templates/`)
                .split('url("./').join(`url("${origin}/templates/`)
                .split('url(./').join(`url(${origin}/templates/`);
            const map = buildCscVarMap({
                customer: { name: customerName, phone: customerPhone, email: customerEmail, address: customerAddress },
                system: { kwp, systemCost: Number(systemCost), tariffRate, omAnnual: Number(omAnnual), inverterKw: Number(inverterKw), inverterBrand, inverterBrands, panelWatt: Number(panelWatt) || 650, atapPrices, phaseMode: sharedPhaseMode },
                math,
                sales,
                dates: { issued: todayDMY(), valid: plus30DMY() },
                financing: { loanPrincipal: parseFloat(loanPrincipal) || undefined, includeSARE, includeSARE10, includeOutright, includeLoan },
                addOn: parseFloat(addOnPrice) || 0,
                site: siteState,
                photos: sitePhotos,
                maxDemand: parseFloat(maxDemandKw) || 0,
                batteryKwh: parseFloat(batteryKwh) || 0,
            });
            let populated = postProcessCscProposal(cscSubstitute(tpl, map), { includeSARE, includeSARE10, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0 });
            // Inject floating Print button + auto-hide on print
            const printUi = `
<style>
  .csc-print-fab {
    position: fixed; bottom: 24px; right: 24px; z-index: 9999;
    background: linear-gradient(135deg, #1A8CFF, #0F62CC); color: #fff;
    border: none; border-radius: 999px; padding: 12px 22px;
    font: 600 14px/1 'Poppins', -apple-system, sans-serif; letter-spacing: 0.02em;
    box-shadow: 0 8px 20px rgba(15, 98, 204, 0.35); cursor: pointer;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .csc-print-fab:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(15, 98, 204, 0.45); }
  .csc-print-fab:active { transform: translateY(0); }
  .csc-print-fab:disabled { opacity: 0.55; cursor: progress; }
  .csc-print-fab svg { width: 18px; height: 18px; fill: currentColor; }
  @media print { .csc-print-fab { display: none !important; } }
</style>
<button class="csc-print-fab" onclick="window.print()" title="Print or Save as PDF">
  <svg viewBox="0 0 24 24"><path d="M19 8h-1V3H6v5H5a3 3 0 0 0-3 3v6h4v4h12v-4h4v-6a3 3 0 0 0-3-3zM8 5h8v3H8V5zm8 14H8v-5h8v5zm3-7a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/></svg>
  Print / Save PDF
</button>`;
            populated = populated.replace('</body>', `${printUi}\n</body>`);
            // Browser's Save-as-PDF defaults the filename to the <title>.
            // Inject "{Customer} - {kWp}KWP Astern Commercial Proposal" so
            // the print path matches the Quick Download filename format.
            const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
            const kwpLabel = `${Number(kwp) || 0}`;
            const pdfTitle = `${safeName} - ${kwpLabel}KWP Astern Commercial Proposal`;
            populated = populated.replace(/<title>[\s\S]*?<\/title>/, `<title>${pdfTitle.replace(/</g, '&lt;')}</title>`);
            const blob = new Blob([populated], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) { setError('Pop-up blocked — allow pop-ups for this site, then click again.'); return; }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            console.error(e);
            setError(`Preview failed: ${e.message || e}`);
        }
    };

    return (
        <Box sx={{ p: 2, backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 980, mx: 'auto' }}>
                {/* ── Inputs (full-width, stacked above preview) ── */}
                <Box sx={{ width: '100%' }}>
                    <Card sx={{ p: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <SectionTitle>👤 Customer / Site</SectionTitle>
                        <Box sx={{ mb: 1.2 }}>
                            <Label required>Company / Customer Name</Label>
                            <TextField fullWidth size="small" value={customerName} onChange={(e) => { setCustomerName(e.target.value); setCustomerTouched(true); }} placeholder="e.g. Frontline Engineering Sdn Bhd"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Label>Phone</Label>
                                <TextField fullWidth size="small" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="012-3456789"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Label>Email</Label>
                                <TextField fullWidth size="small" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="name@example.com"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            </Box>
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Site Address</Label>
                            <TextField fullWidth multiline minRows={2} size="small" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Full installation address"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                        </Box>

                        <SectionTitle>⚡ System</SectionTitle>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Label>No. of Panels</Label>
                                <TextField fullWidth size="small" type="number" value={panels} onChange={(e) => setPanels(e.target.value)} placeholder="e.g. 150"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Label>Panel Watt</Label>
                                <FormControl fullWidth size="small">
                                    <Select value={panelWatt} onChange={(e) => setPanelWatt(e.target.value)}
                                        sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                        <MenuItem value="620" sx={{ fontFamily: PP }}>620 Wp</MenuItem>
                                        <MenuItem value="640" sx={{ fontFamily: PP }}>640 Wp</MenuItem>
                                        <MenuItem value="650" sx={{ fontFamily: PP }}>650 Wp</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Label required>kWp</Label>
                                <TextField fullWidth size="small" type="number" value={kwp} onChange={(e) => { setKwp(e.target.value); setKwpTouched(true); setPanels(''); }} placeholder="e.g. 96"
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            </Box>
                        </Box>
                        {panels && panelWatt && (
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.primary, fontFamily: PP, mt: -0.6, mb: 1.2 }}>
                                Auto: {panels} × {panelWatt} Wp = {(Number(panels) * Number(panelWatt) / 1000).toFixed(2)} kWp
                            </Typography>
                        )}
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Inverter Size (kW) <span style={{ color: COLORS.sub, fontWeight: 400 }}>(auto from kWp · editable)</span></Label>
                            <TextField fullWidth size="small" type="number" value={inverterKw}
                                onChange={(e) => { setInverterKw(e.target.value); setInverterKwTouched(true); }}
                                placeholder="auto"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            {inverterKw && (
                                <Typography sx={{ fontSize: '0.68rem', color: inverterKwTouched ? COLORS.warning : COLORS.primary, fontFamily: PP, mt: 0.4 }}>
                                    {inverterKwTouched
                                        ? `Manual override · ${inverterKw} kW will appear in the proposal`
                                        : `Auto: ${autoInverterKw(kwp, atapPrices, sharedPhaseMode)} kW from ${kwp || '—'} kWp (${sharedPhaseMode === '3phase' ? '3-phase' : 'single-phase'})`}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Battery Size (kWh) <span style={{ color: COLORS.sub, fontWeight: 400 }}>(optional)</span></Label>
                            <TextField fullWidth size="small" type="number" value={batteryKwh}
                                onChange={(e) => setBatteryKwh(e.target.value)}
                                placeholder="e.g. 16 (leave blank for no battery)"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                If set, a Battery line is added to the Bill of Material (page 22) and the cover system spec.
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Inverter Brand <span style={{ color: COLORS.sub, fontWeight: 400 }}>(drives commercial RM/kWp)</span></Label>
                            <ToggleButtonGroup
                                value={inverterBrands}
                                onChange={(_, v) => {
                                    if (!v || v.length === 0) return;  // never allow deselecting all
                                    setInverterBrands(v);
                                    setInverterBrand(v[0]);
                                    setBrandTouched(true);
                                }}
                                size="small"
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.72rem', textTransform: 'none', py: 0.5, px: 1.5, borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}
                            >
                                {[['goodwe','GoodWe'],['huawei','Huawei']]
                                    .map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
                            </ToggleButtonGroup>
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                Click to toggle &mdash; multi-select supported. First selected drives pricing. GoodWe at every tier; Huawei &gt;100 kWp only. Battery requires GoodWe Hybrid. Outside range, auto-fill falls back to RM {String(DEFAULT_PRICE_PER_KWP)}/kWp.
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label required>System Price <span style={{ color: COLORS.sub, fontWeight: 400 }}>(both editable · two-way synced)</span></Label>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Per kWp (RM)</Typography>
                                    <TextField fullWidth size="small" type="number" value={pricePerKwp}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setPricePerKwp(v);
                                            setPriceMode('kwp');
                                            setSystemCostTouched(true);
                                            const k = Number(kwp) || 0;
                                            setSystemCost(v && k ? String(Math.round(Number(v) * k)) : '');
                                        }}
                                        placeholder={`e.g. ${DEFAULT_PRICE_PER_KWP}`}
                                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Total Price (RM)</Typography>
                                    <TextField fullWidth size="small" type="number" value={systemCost}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setSystemCost(v);
                                            setPriceMode('total');
                                            setSystemCostTouched(true);
                                            const k = Number(kwp) || 0;
                                            setPricePerKwp(v && k ? String(Math.round(Number(v) / k)) : '');
                                        }}
                                        placeholder={kwp ? `auto ${fmt(Number(kwp) * DEFAULT_PRICE_PER_KWP)}` : 'e.g. 172800'}
                                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                                </Box>
                            </Box>
                            {kwp && systemCost && pricePerKwp && (
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                    {priceMode === 'kwp'
                                        ? `Total: RM ${fmt(Number(systemCost))} for ${kwp} kWp`
                                        : `Per kWp: RM ${fmt(Number(pricePerKwp))}/kWp`}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Add-on Price (optional)</Label>
                            <TextField fullWidth size="small" type="number" value={addOnPrice}
                                onChange={(e) => setAddOnPrice(e.target.value)}
                                placeholder="e.g. extra scope / accessories (RM)"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            {systemCost && (
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                    {(() => {
                                        const base = Number(systemCost) || 0;
                                        const ad = Number(addOnPrice) || 0;
                                        const sst = sstOn(base + ad);
                                        const grand = base + ad + sst;
                                        return `Base RM ${fmt(base)} + Add-on RM ${fmt(ad)} + SST 6% RM ${fmt2(sst)} = Grand Total RM ${fmt2(grand)}`;
                                    })()}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label required>TNB Tariff</Label>
                            <FormControl fullWidth size="small">
                                <Select value={tariffRate} onChange={(e) => { setTariffRate(Number(e.target.value)); setTariffTouched(true); }}
                                    sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                    {CSC_PROPOSAL_TARIFFS.map((t) => (
                                        <MenuItem key={t.rate} value={t.rate} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                            {t.label} — RM {t.rate}/kWh
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <SectionTitle>💵 Financing Scenarios</SectionTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, ml: -1 }}>
                            <FormControlLabel control={<Checkbox checked={includeOutright} onChange={(e) => setIncludeOutright(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Outright Purchase (Cash)</Typography>} />
                            <FormControlLabel control={<Checkbox checked={includeLoan} onChange={(e) => setIncludeLoan(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Loan-financed</Typography>} />
                            {(() => { const _below = (Number(kwp) || 0) > 0 && (Number(kwp) || 0) < 42; return (
                            <><FormControlLabel disabled={_below} control={<Checkbox checked={includeSARE && !_below} onChange={(e) => { setIncludeSARE(e.target.checked); setSareIncludeTouched(true); }} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: _below ? COLORS.sub : undefined }}>SARE 20yr (Solar Lease) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· auto-on for ≥42 kWp</span></Typography>} /><FormControlLabel disabled={_below} control={<Checkbox checked={includeSARE10 && !_below} onChange={(e) => { setIncludeSARE10(e.target.checked); setSareIncludeTouched(true); }} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: _below ? COLORS.sub : undefined }}>SARE 10yr (Solar Lease) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· +8 sen/kWh</span></Typography>} /></>); })()}
                        </Box>
                        {includeLoan && (
                            <Box sx={{ mb: 1.2, p: 1.2, backgroundColor: COLORS.bg, borderRadius: '8px' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Label>Interest Rate % p.a.</Label>
                                        <TextField fullWidth size="small" type="number" value={loanRate} onChange={(e) => setLoanRate(e.target.value)}
                                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Label>Term (months)</Label>
                                        <TextField fullWidth size="small" type="number" value={loanTermMonths} onChange={(e) => setLoanTermMonths(e.target.value)}
                                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.6 }}>
                                    PMT amortization (reducing balance) — matches Astern bank-loan Excel. e.g. RM 172,800 @ 5% × 7yr → RM 2,442/mo · RM 29,306/yr · RM 205,156 total.
                                </Typography>
                            </Box>
                        )}
                        {includeSARE && (
                            <Box sx={{ mb: 1.2 }}>
                                <Label>SARE Tariff (RM/kWh) — auto from kWp tier</Label>
                                <TextField fullWidth size="small" type="number" value={sareTariffOverride} onChange={(e) => { setSareTariffOverride(e.target.value); setSareTouched(true); }}
                                    placeholder={kwp ? (Number(kwp) < 40 ? 'SARE not offered for <40 kWp' : `default RM ${defaultSareTariffByKwp(Number(kwp), 20, _sareAddOnCost)}/kWh (${Number(kwp) <= 150 ? '40-150' : Number(kwp) <= 300 ? '151-300' : Number(kwp) <= 600 ? '301-600' : '> 600'} kWp tier${_sareAddOnCost > 0 ? ' + add-ons' : ''})`) : 'Enter kWp above to auto-fill'}
                                    InputProps={{ sx: { fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                    Tiers: ≤150 kWp · RM 0.36 &nbsp;|&nbsp; ≤300 · RM 0.32 &nbsp;|&nbsp; ≤600 · RM 0.30 &nbsp;|&nbsp; &gt;600 · RM 0.28
                                </Typography>
                            </Box>
                        )}

                        <SectionTitle>🧾 Tax Incentives</SectionTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, ml: -1 }}>
                            <FormControlLabel control={<Checkbox checked={includeCA} onChange={(e) => setIncludeCA(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Capital Allowance (CA) — 6-year absorption</Typography>} />
                            <FormControlLabel disabled={(Number(kwp) || 0) < 40} control={<Checkbox checked={includeITA && (Number(kwp) || 0) >= 40} onChange={(e) => { setIncludeITA(e.target.checked); setItaTouched(true); }} size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Investment Tax Allowance (GITA) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· {(Number(kwp) || 0) < 40 ? '≥40 kWp only' : 'default on'}</span></Typography>} />
                        </Box>

                        <SectionTitle>🗺️ Site Location &amp; Design</SectionTitle>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>State (drives irradiance page)</Label>
                            <FormControl fullWidth size="small">
                                <Select value={siteState} onChange={(e) => setSiteState(e.target.value)} displayEmpty
                                    sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                    <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Not specified —</MenuItem>
                                    {Object.entries(MY_STATE_GHI).map(([k, v]) => (
                                        <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                            {v.name} <span style={{ color: COLORS.sub, marginLeft: 6 }}>· {v.ghi.toFixed(1)} kWh/m²/day</span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                GHI values from SolarGIS Malaysia map. Generation projection uses fixed 3.4 peak sun hours (aligned to Bill tab / commercial calculator).
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 1.2 }}>
                            <Label>Maximum Demand (kW)</Label>
                            <TextField fullWidth size="small" type="number" value={maxDemandKw}
                                onChange={(e) => setMaxDemandKw(e.target.value)}
                                placeholder="e.g. 50 (from TNB bill)"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
                            {(kwp || inverterKw) && (
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                    {(() => {
                                        const md = Number(maxDemandKw) || 0;
                                        const inv = Number(inverterKw) || 0;
                                        if (!md) return 'MD missing — Site Design page defaults to ATAP.';
                                        if (!inv) return 'Inverter Size missing — enter it above for the SELCO/ATAP recommendation.';
                                        return inv > md
                                            ? `Inverter ${inv} kW > ${md} kW MD → SELCO (zero export)`
                                            : `Inverter ${inv} kW ≤ ${md} kW MD → ATAP (sells excess at SMP rate)`;
                                    })()}
                                </Typography>
                            )}
                        </Box>

                        <SectionTitle>📸 Site Photos (optional, up to 10)</SectionTitle>
                        <Box sx={{ mb: 1.2 }}>
                            <Button variant="outlined" component="label" size="small"
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px' }}>
                                Add photo(s)
                                <input type="file" hidden accept="image/*" multiple
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        if (!files.length) return;
                                        const slotsLeft = Math.max(0, 10 - sitePhotos.length);
                                        const toRead = files.slice(0, slotsLeft);
                                        Promise.all(toRead.map((f) => new Promise((resolve) => {
                                            const fr = new FileReader();
                                            fr.onload = () => resolve(fr.result);
                                            fr.onerror = () => resolve(null);
                                            fr.readAsDataURL(f);
                                        }))).then((urls) => {
                                            setSitePhotos((prev) => [...prev, ...urls.filter(Boolean)].slice(0, 10));
                                        });
                                        e.target.value = '';
                                    }} />
                            </Button>
                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4, display: 'inline-block', ml: 1 }}>
                                {sitePhotos.length === 0 ? 'No photos — the Site Survey page will be omitted.' : `${sitePhotos.length} / 10 selected`}
                            </Typography>
                            {sitePhotos.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                                    {sitePhotos.map((src, i) => (
                                        <Box key={i} sx={{ position: 'relative', width: 64, height: 64, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                                            <img src={src} alt={`Site ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <Button size="small" onClick={() => setSitePhotos((prev) => prev.filter((_, j) => j !== i))}
                                                sx={{ position: 'absolute', top: -4, right: -4, minWidth: 0, width: 20, height: 20, p: 0, fontSize: '0.7rem', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: '50%', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
                                                ×
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Box>

                {/* ── Preview + Output (stacked below the form) ── */}
                <Box sx={{ width: '100%' }}>
                    <Card sx={{ p: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>📊 Live Projection</Typography>
                            <ToggleButtonGroup exclusive value={lang} onChange={(_, v) => v && setLang(v)} size="small">
                                <ToggleButton value="en" sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none' }}>🇬🇧 EN</ToggleButton>
                                <ToggleButton value="cn" sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none' }}>🇨🇳 中文</ToggleButton>
                                <ToggleButton value="my" sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none' }}>🇲🇾 BM</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {!headlines ? (
                            <Box sx={{ p: 4, textAlign: 'center', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
                                <Typography sx={{ color: COLORS.sub, fontFamily: PP, fontSize: '0.9rem' }}>
                                    Enter kWp + System Price to see the 20-year projection.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.success}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Year-1 Generation</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>{fmt(headlines.yr1Gen)} kWh</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.primary}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Avg Monthly Saving</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>RM {fmt(headlines.avgMonthly)}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.warning}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Payback (with CA/GITA)</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>{headlines.payback != null ? `${headlines.payback} years` : '—'}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.purple}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>IRR</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>{headlines.irr != null ? `${(headlines.irr * 100).toFixed(1)}%` : '—'}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.success}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>20-Year Total Saving</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>RM {fmt(headlines.total20yr)}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid ${COLORS.text}`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>LCOE</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>RM {fmt3(headlines.lcoe)}/kWh</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid #16a34a`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>CO₂ Reduced</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>{fmt(headlines.co2)} t/yr</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Box sx={{ p: 1.5, borderLeft: `4px solid #16a34a`, backgroundColor: COLORS.bg, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Trees saved</Typography>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: COLORS.text, fontFamily: PP }}>{fmt(headlines.trees)} / yr</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>20-Year Projection Sample (Outright + CA)</Typography>
                                <Box sx={{ overflowX: 'auto', borderRadius: '6px', border: `1px solid ${COLORS.border}` }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP, fontSize: '0.78rem' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: COLORS.text, color: '#fff' }}>
                                                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Year</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Generation</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Savings</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'right' }}>CA Tax</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Annual CF</th>
                                                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Cumulative</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(math.years || []).slice(0, 10).map((y) => (
                                                <tr key={y.year} style={{ backgroundColor: y.year % 2 === 0 ? COLORS.bg : '#fff' }}>
                                                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>{y.year}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmt(y.generation)} kWh</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>RM {fmt(y.grossRevenue)}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', color: COLORS.purple }}>{y.caTaxSaving > 0 ? `RM ${fmt(y.caTaxSaving)}` : '—'}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>RM {fmt(y.outright.annualCF)}</td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', color: y.outright.cumulative >= 0 ? COLORS.success : '#ef4444', fontWeight: 600 }}>RM {fmt(y.outright.cumulative)}</td>
                                                </tr>
                                            ))}
                                            {(math.years || []).length > 10 && (
                                                <tr><td colSpan={6} style={{ padding: '6px 10px', textAlign: 'center', color: COLORS.sub, fontStyle: 'italic' }}>...full 20-year table appears in the rendered proposal PDF</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </Box>
                            </>
                        )}
                    </Card>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Card sx={{ p: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1.5 }}>📄 Generate Proposal</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PrintIcon />}
                                onClick={printPreview}
                                disabled={generating}
                                sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', px: 3 }}
                            >
                                Print Preview (Ctrl+P → Save as PDF)
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={generating ? <CircularProgress size={16} /> : <DescriptionIcon />}
                                onClick={generatePdf}
                                disabled={generating}
                                sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', px: 3 }}
                            >
                                {generating ? 'Generating…' : 'Download PDF'}
                            </Button>
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: COLORS.sub, fontFamily: PP, mt: 1.5 }}>
                            Print Preview opens in a new tab with vector-quality text — use the browser's built-in "Save as PDF" for the crispest output. Download PDF rasterizes via html2canvas (good for mobile).
                        </Typography>
                    </Card>
                </Box>
        </Box>
    );
}
